import { HPOOption } from '@/types/RequisitionFormTypes'
import React, { useEffect, useRef, useState } from 'react'
import { extractHPOId } from './hpoTypes'
import { hpoAPIClient } from './HPOAPIClient'

interface HPOSearchProps {
  id: string
  value: HPOOption[]
  onChange: (selectedOptions: HPOOption[]) => void
  placeholder?: string
  label?: string
  required?: boolean
  className?: string
  maxSelections?: number
}

const HPOSearch: React.FC<HPOSearchProps> = ({
  value,
  onChange,
  placeholder = 'Search for HPO terms...',
  className = '',
  maxSelections,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<HPOOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Debounced search via API
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      return
    }

    const timeoutId = setTimeout(async () => {
      abortControllerRef.current?.abort()
      abortControllerRef.current = new AbortController()
      const signal = abortControllerRef.current.signal
      setIsLoading(true)
      try {
        const codes = await hpoAPIClient.searchHPO(searchQuery.trim(), 20, signal)
        const options: HPOOption[] = codes.map((c) => ({
          id: extractHPOId(c.code),
          label: c.description,
          node: { id: extractHPOId(c.code), lbl: c.description },
        }))
        setSearchResults(options)
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          console.error('HPO search error:', error)
          setSearchResults([])
        }
      } finally {
        setIsLoading(false)
      }
    }, 300)

    return () => {
      clearTimeout(timeoutId)
      abortControllerRef.current?.abort()
    }
  }, [searchQuery])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setHighlightedIndex(-1)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputFocus = () => {
    setIsOpen(true)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
    setHighlightedIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev < searchResults.length - 1 ? prev + 1 : prev))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIndex >= 0 && searchResults[highlightedIndex]) {
        handleOptionSelect(searchResults[highlightedIndex])
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
      setHighlightedIndex(-1)
    }
  }

  const handleOptionSelect = (option: HPOOption) => {
    const isAlreadySelected = value.some((selected) => selected.id === option.id)

    if (isAlreadySelected) {
      // Remove if already selected
      const newValue = value.filter((selected) => selected.id !== option.id)
      onChange(newValue)
    } else {
      // Check if max selections reached
      if (maxSelections && value.length >= maxSelections) {
        return
      }
      // Add if not selected
      const newValue = [...value, option]
      onChange(newValue)
    }

    setSearchQuery('')
    setSearchResults([])
    setHighlightedIndex(-1)
    inputRef.current?.focus()
  }

  const handleRemoveOption = (optionToRemove: HPOOption) => {
    const newValue = value.filter((option) => option.id !== optionToRemove.id)
    onChange(newValue)
  }

  const isOptionSelected = (option: HPOOption) => {
    return value.some((selected) => selected.id === option.id)
  }

  const isMaxSelectionsReached = maxSelections ? value.length >= maxSelections : false

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      <div className="relative">
        {/* Selected options display */}
        <div className="min-h-[42px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 focus-within:border-indigo-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-500">
          <div className="flex flex-wrap gap-1">
            {value.map((option) => (
              <span
                key={option.id}
                className="inline-flex items-center rounded-md bg-blue-100 px-2 py-1 text-sm text-blue-800"
              >
                <span className="mr-1 font-mono text-xs text-blue-600">{option.id}</span>
                {option.label}
                <button
                  type="button"
                  onClick={() => handleRemoveOption(option)}
                  className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600"
                >
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </span>
            ))}

            {/* Search input */}
            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onKeyDown={handleKeyDown}
              placeholder={
                isMaxSelectionsReached
                  ? `Maximum ${maxSelections} selections reached`
                  : value.length === 0
                    ? placeholder
                    : 'Add more HPO terms...'
              }
              disabled={isMaxSelectionsReached}
              className="min-w-0 flex-1 border-none bg-transparent text-sm outline-none disabled:opacity-50"
            />
          </div>
        </div>

        {/* Dropdown arrow */}
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown options */}
      {isOpen && (
        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-300 bg-white shadow-lg">
          {isLoading ? (
            <div className="px-3 py-2 text-sm text-gray-500">Searching HPO terms...</div>
          ) : searchResults.length > 0 ? (
            <div>
              {searchResults.map((option, index) => (
                <div
                  key={option.id}
                  className={`cursor-pointer px-3 py-2 text-sm ${
                    highlightedIndex === index ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-100'
                  } ${isOptionSelected(option) ? 'bg-blue-50' : ''}`}
                  onClick={() => handleOptionSelect(option)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <span className="mr-2 font-mono text-xs text-blue-600">{option.id}</span>
                        <span className="font-medium">{option.label}</span>
                      </div>
                    </div>
                    {isOptionSelected(option) && (
                      <svg className="ml-2 h-4 w-4 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : searchQuery ? (
            <div className="px-3 py-2 text-sm text-gray-500">No HPO terms found</div>
          ) : (
            <div className="px-3 py-2 text-sm text-gray-500">Start typing to search HPO terms...</div>
          )}
        </div>
      )}
    </div>
  )
}

export default HPOSearch
